import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import ReactECharts from 'echarts-for-react';
import { format, subDays, startOfDay } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDownload, FiCalendar, FiBarChart3, FiPieChart, FiTrendingUp } = FiIcons;

function SystemReports() {
  const { prisons, orders, inventory } = useApp();
  const [dateRange, setDateRange] = useState('7');
  const [reportType, setReportType] = useState('revenue');

  // Generate sample data for demonstration
  const generateTimeSeriesData = () => {
    const days = parseInt(dateRange);
    const data = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
      const dayOrders = Math.floor(Math.random() * 20) + 5;
      const dayRevenue = dayOrders * (Math.random() * 15 + 10);
      
      data.push({
        date,
        orders: dayOrders,
        revenue: dayRevenue
      });
    }
    
    return data;
  };

  const timeSeriesData = generateTimeSeriesData();

  const revenueChartOption = {
    title: {
      text: 'Revenue Trend',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: timeSeriesData.map(item => format(new Date(item.date), 'MMM dd'))
    },
    yAxis: {
      type: 'value',
      name: 'Revenue (£)'
    },
    series: [
      {
        data: timeSeriesData.map(item => item.revenue.toFixed(2)),
        type: 'line',
        smooth: true,
        itemStyle: {
          color: '#0ea5e9'
        },
        areaStyle: {
          color: 'rgba(14, 165, 233, 0.1)'
        }
      }
    ]
  };

  const ordersChartOption = {
    title: {
      text: 'Orders Trend',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: timeSeriesData.map(item => format(new Date(item.date), 'MMM dd'))
    },
    yAxis: {
      type: 'value',
      name: 'Orders'
    },
    series: [
      {
        data: timeSeriesData.map(item => item.orders),
        type: 'bar',
        itemStyle: {
          color: '#10b981'
        }
      }
    ]
  };

  const categoryData = [
    { name: 'Sandwiches', value: 35 },
    { name: 'Hot Drinks', value: 25 },
    { name: 'Cold Drinks', value: 20 },
    { name: 'Snacks', value: 20 }
  ];

  const categoryChartOption = {
    title: {
      text: 'Popular Categories',
      left: 'center'
    },
    tooltip: {
      trigger: 'item'
    },
    legend: {
      orient: 'vertical',
      left: 'left'
    },
    series: [
      {
        name: 'Orders',
        type: 'pie',
        radius: ['40%', '70%'],
        data: categoryData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };

  const handleExport = () => {
    // In a real application, this would generate and download actual reports
    const reportData = {
      dateRange,
      reportType,
      generatedAt: new Date().toISOString(),
      data: timeSeriesData,
      summary: {
        totalOrders: timeSeriesData.reduce((sum, item) => sum + item.orders, 0),
        totalRevenue: timeSeriesData.reduce((sum, item) => sum + item.revenue, 0),
        averageOrderValue: timeSeriesData.reduce((sum, item) => sum + item.revenue, 0) / timeSeriesData.reduce((sum, item) => sum + item.orders, 0)
      }
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ab-orders-report-${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const totalOrders = timeSeriesData.reduce((sum, item) => sum + item.orders, 0);
  const totalRevenue = timeSeriesData.reduce((sum, item) => sum + item.revenue, 0);
  const averageOrderValue = totalRevenue / totalOrders;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-government-900">
            System Reports
          </h1>
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            <SafeIcon icon={FiDownload} className="w-5 h-5" />
            <span>Export Report</span>
          </button>
        </div>
        
        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiCalendar} className="w-5 h-5 text-government-600" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiBarChart3} className="w-5 h-5 text-government-600" />
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="revenue">Revenue Report</option>
              <option value="orders">Orders Report</option>
              <option value="inventory">Inventory Report</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-government-600 mb-1">
                Total Orders
              </p>
              <p className="text-2xl font-bold text-government-900">
                {totalOrders}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100">
              <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-government-600 mb-1">
                Total Revenue
              </p>
              <p className="text-2xl font-bold text-government-900">
                £{totalRevenue.toFixed(2)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-100">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-government-600 mb-1">
                Avg Order Value
              </p>
              <p className="text-2xl font-bold text-government-900">
                £{averageOrderValue.toFixed(2)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-100">
              <SafeIcon icon={FiPieChart} className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <ReactECharts 
            option={reportType === 'revenue' ? revenueChartOption : ordersChartOption} 
            style={{ height: '400px' }} 
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <ReactECharts option={categoryChartOption} style={{ height: '400px' }} />
        </motion.div>
      </div>

      {/* Detailed Data Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-bold text-government-900 mb-4">
          Detailed Data
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-government-200">
                <th className="text-left py-3 px-4 font-medium text-government-900">Date</th>
                <th className="text-left py-3 px-4 font-medium text-government-900">Orders</th>
                <th className="text-left py-3 px-4 font-medium text-government-900">Revenue</th>
                <th className="text-left py-3 px-4 font-medium text-government-900">Avg Order Value</th>
              </tr>
            </thead>
            <tbody>
              {timeSeriesData.map((item, index) => (
                <tr key={index} className="border-b border-government-100 hover:bg-government-50">
                  <td className="py-3 px-4 text-government-900">
                    {format(new Date(item.date), 'MMM dd, yyyy')}
                  </td>
                  <td className="py-3 px-4 text-government-900">{item.orders}</td>
                  <td className="py-3 px-4 text-government-900">£{item.revenue.toFixed(2)}</td>
                  <td className="py-3 px-4 text-government-900">£{(item.revenue / item.orders).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}

export default SystemReports;